import Vue from 'vue'
import Router from 'vue-router'
import { Auth } from 'aws-amplify'
import store from '@/store/'
import authRoutes from '@/routes/auth'
import guestRoutes from '@/routes/guest'
import commonRoutes from '@/routes/common'
import { renderError } from '@/utils/error'

Vue.use(Router)

/**
 * ログイン必須ナビゲーションガード
 */
async function requireAuth(to, from, next) {
  const user = await Auth.currentUserInfo()

  if (user) {
    store.commit('auth/SET_ATTEMPT', null)

    return next()
  }

  // アクセスしようとしていたURLを記録
  store.commit('auth/SET_ATTEMPT', to.fullPath)

  // 案件IDを含む場合
  const { businessId } = to.params
  if (businessId) {
    return next({ name: 'business.auth.login', params: { businessId } })
  }

  next({ name: 'auth.login' })
}

/**
 * ログイン不可ナビゲーションガード
 */
async function requireGuest(to, from, next) {
  const user = await Auth.currentUserInfo()

  if (user) {
    return next({ name: 'index' })
  }

  next()
}

/**
 * ルート定義に一括でガードを適用する
 * @param {array} routes ルート定義
 * @param {function} beforeEachFunc ナビゲーションガード関数
 */
function guardAll(routes, beforeEachFunc) {
  return routes.map(route => {
    route.beforeEnter = beforeEachFunc
    return route
  })
}

/**
 * 案件ID付きのルート定義を追加する
 * @param {array} routes ルート定義
 * @returns {array} 案件ID付きルート定義
 */
function addBusinessId(routes) {
  const businessRoutes = []

  for (const route of routes) {
    if (route.name === 'fallback') {
      continue
    }

    businessRoutes.push(
      Object.assign({}, route, {
        path: `/:businessId(\\d+)${route.path}`,
        name: `business.${route.name}`
      })
    )
  }

  return [...routes, ...businessRoutes]
}

const fallback = {
  path: '*',
  name: 'fallback',
  redirect: { name: 'error.not-found' }
}

const authRoutesWithBusinessId = addBusinessId(authRoutes)
const guestRoutesWithBusinessId = addBusinessId(guestRoutes)
const commonRoutesWithBusinessId = addBusinessId(commonRoutes)

const routes = [
  ...guardAll(authRoutesWithBusinessId, requireAuth),
  ...guardAll(guestRoutesWithBusinessId, requireGuest),
  ...commonRoutesWithBusinessId,
  fallback
]

const router = new Router({
  mode: 'history',
  routes,
  scrollBehavior(to, from) {
    if (to.path !== from.path) {
      return { x: 0, y: 0 }
    }
  }
})

// URL中の案件IDに対する処理
router.beforeEach((to, from, next) => {
  if (!store.getters['auth/check']) {
    return next()
  }

  const { businessId } = to.params
  const isStaff = store.getters['auth/isStaff']

  // 案件IDあり
  if (businessId || businessId === 0) {
    // 新郎新婦の場合は、案件IDなしにリダイレクト
    if (!isStaff) {
      // 案件ID以外のパラメータを引き継ぐ
      const params = Object.assign({}, to.params)
      delete params.businessId
      return next({ name: to.name.replace('business.', ''), params })
    }

    // プランナー・パートナーの場合は、URLから案件IDをセットし直す
    store.commit('auth/SET_BUSINESS_ID', businessId)

    return next()
  }

  // 新郎新婦/施設ユーザーもしくは案件ID不要ルートであればそのまま通過
  if (!isStaff || isNoBusinessRoute(to)) {
    return next()
  }

  // プランナー・パートナーでURLに案件IDなしの場合は、エラー画面へ
  return next({ name: 'error.no-business' })
})

/**
 * 案件ID不要ルート判定
 * @param {Router} route VueRouterのRouteオブジェクト
 * @returns {boolean} 案件IDなしでアクセスしていい画面か？
 */
function isNoBusinessRoute(route) {
  return (
    // エラー画面は案件IDなしで通す
    // 無限ループになる場合があるため
    route.name.indexOf('error.') === 0 ||
    route.name === 'index' ||
    route.name.indexOf('visit.') === 0 ||
    route.name.indexOf('use-history.') === 0
  )
}

/**
 * 操作可否判定
 */

// メニューになくても通過OKなルート
const whitelist = [
  'index',
  'business.index',
  'static.index',
  'business.static.index',
  'logout',
  'business.logout',
]

const parentChildMap = (() => {
  const map = {}
  for (const route of routes) {
    if (typeof route.parent === 'undefined') {
      continue
    }
    map[route.name] = route.parent
  }
  return map
})()

/**
 * 同一のルート名か？
 * @param {string} a ルート名
 * @param {string} b ルート名
 */
function isSameRouteName(a, b) {
  // 案件IDの有無を同一とみなす
  return a === b || `business.${a}` === b
}

/**
 * メニューからアクセス可否を判定する
 * @param {Router} to アクセスしたいルート
 * @param {array}} paths メニューに含まれるURL
 */
function canAccess(to, paths) {
  for (const path of paths) {
    const { resolved } = router.resolve(path)

    if (isSameRouteName(to.name, resolved.name)) {
      return true
    }

    const parentName = parentChildMap[to.name]

    if (parentName && isSameRouteName(parentName, resolved.name)) {
      return true
    }
  }
  return false
}

router.beforeResolve(async (to, from, next) => {
  // ログイン前画面、共通画面はOK
  if (!authRoutesWithBusinessId.map(r => r.name).includes(to.name)) {
    return next()
  }

  // メニュー更新
  const businessId = store.getters['auth/businessId']
  try {
    await store.dispatch('app/fetchMenu', businessId)
  } catch (err) {
    renderError(err)
    return next(false)
  }

  if (whitelist.includes(to.name)) {
    return next()
  }

  // メニューチェック
  const paths = store.getters['app/menus'].map(m => m.url.path)
  if (canAccess(to, paths)) {
    return next()
  }

  // 権限エラー
  return businessId
    ? next({ name: 'business.error.auth', params: { businessId } })
    : next({ name: 'error.auth' })
})

export default router

export function appRoot() {
  const root = store.getters['app/root']

  if (root.external) {
    window.location.href = root.path
    return false
  }

  router.push(root.path)
}
